import React, { createContext, useContext, useMemo } from 'react'
import Cookies from 'universal-cookie'
import { HttpError } from './HttpError'

interface RequestOptions extends Omit<RequestInit, 'method' | 'headers' | 'body'> {
  url: string
  token?: () => string
  payload?: Record<string, unknown>
}

const getHeaders = (lazyToken?: () => string): Record<string, string> => {
  return {
    'Accept': 'application/json',
    'Content-Type': 'application/json',
    ...lazyToken && { 'Authorization': `Token ${lazyToken()}` }
  }
}

const getUploadHeaders = (lazyToken?: () => string): Record<string, string> => {
  return {
    'Accept': 'application/json',
    ...lazyToken && { 'Authorization': `Token ${lazyToken()}` }
  }
}

const doFetch = async (fetcher: () => Promise<Response>, dataResolver: 'json' | 'arrayBuffer' = 'json', doThrow = true) => {
  const response = await fetcher()

  if (!response.ok && doThrow) {
    throw new HttpError(response.status, response)
  }
  try {
    return await response[dataResolver]()
  } catch (err) {
    return false
  }
}

// Core HTTP methods — used directly by Auth.ts with explicit token
export const get = ({ url, token, ...rest }: RequestOptions) => doFetch(() => fetch(url, {
  method: 'GET',
  headers: getHeaders(token),
  ...rest
}))

export const post = ({ url, payload, token, ...rest }: RequestOptions) => doFetch(() => fetch(url, {
  method: 'POST',
  headers: getHeaders(token),
  body: JSON.stringify(payload),
  ...rest
}))

export const put = ({ url, payload, token, ...rest }: RequestOptions) => doFetch(() => fetch(url, {
  method: 'PUT',
  headers: getHeaders(token),
  body: JSON.stringify(payload),
  ...rest
}))

export const destroy = ({ url, token, ...rest }: RequestOptions) => doFetch(() => fetch(url, {
  method: 'DELETE',
  headers: getHeaders(token),
  ...rest
}))

// Authenticated HTTP interface provided via React context
export interface AuthenticatedHttp {
  apiGet: (url: string) => Promise<unknown>
  apiPost: (url: string, payload?: Record<string, unknown>) => Promise<unknown>
  apiPut: (url: string, payload?: Record<string, unknown>) => Promise<unknown>
  apiDestroy: (url: string) => Promise<unknown>
  upload: (url: string, file: File) => Promise<unknown>
  getFile: (url: string) => Promise<unknown>
}

const HttpContext = createContext<AuthenticatedHttp | undefined>(undefined)

export function HttpProvider({ sessionCookieName, children }: { sessionCookieName: string; children: React.ReactNode }) {
  const value = useMemo(() => {
    const lazyToken = () => {
      const cookies = new Cookies()
      return cookies.get(sessionCookieName)
    }
    return {
      apiGet: (url: string) => get({ url, token: lazyToken }),
      apiPost: (url: string, payload?: Record<string, unknown>) => post({ url, payload, token: lazyToken }),
      apiPut: (url: string, payload?: Record<string, unknown>) => put({ url, payload, token: lazyToken }),
      apiDestroy: (url: string) => destroy({ url, token: lazyToken }),
      upload: (url: string, file: File) => {
        const data = new FormData()
        data.append('file', file)
        return doFetch(() => fetch(url, {
          method: 'POST',
          headers: getUploadHeaders(lazyToken),
          body: data,
        }), 'json', false)
      },
      getFile: (url: string) => {
        const headers: Record<string, string> = {
          'Accept': '*/*',
          'Authorization': `Token ${lazyToken()}`
        }
        return doFetch(() => fetch(url, {
          method: 'GET',
          headers,
        }), 'arrayBuffer')
      },
    }
  }, [sessionCookieName])

  return React.createElement(HttpContext.Provider, { value }, children)
}

export function useHttp(): AuthenticatedHttp {
  const context = useContext(HttpContext)
  if (!context) {
    throw new Error('useHttp must be used within an HttpProvider')
  }
  return context
}
